import * as React from 'react';
import {Mutation} from 'react-apollo';

import gql from 'graphql-tag';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';
import type {GraphQLErrorObject} from 'nutshell-core/utils/graphql-errors';

import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import type {
    CreateStageset as CreateStagesetMutation,
    CreateStagesetVariables as CreateStagesetMutationVariables,
    UpdateStageset as UpdateStagesetMutation,
    UpdateStagesetVariables as UpdateStagesetMutationVariables,
    CreateStagesetInputFieldInput,
    UpdateStagesetInputFieldInput,
    StagesetsForPipelineList as StagesetsForPipelineListQuery,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import STAGESET_BASIC from '../graphql/fragments/stageset-basic.graphql';
import UPDATE_STAGESET from '../graphql/mutations/update-stageset.graphql';
import GET_STAGESETS from '../graphql/queries/get-stagesets-for-pipeline-list.graphql';

import styles from './pipeline-name-popup.module.css';

type OwnProps = {
    onClose: (e?: React.SyntheticEvent<any>) => any;
    stageset?: {
        id: string;
        name: string;
    };
};

type State = {
    isSaving: boolean;
    errorMessage?: string;
    pipelineName?: string;
};

type GraphQLProps = {
    createStageset: (arg1: CreateStagesetInputFieldInput) => Promise<any>;
    updateStageset: (arg1: UpdateStagesetInputFieldInput) => Promise<any>;
};
type Props = OwnProps & GraphQLProps;

export class PipelineNamePopupComponent extends React.PureComponent<Props, State> {
    nameTextFieldRef: HTMLInputElement | null | undefined;

    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isSaving: false,
            errorMessage: undefined,
            pipelineName: undefined,
        };
    }

    render() {
        return (
            // @ts-expect-error
            <form onSubmit={this.handleSubmit} className={styles['create-new-pipeline-form']}>
                <SingleInputModal
                    title={this.props.stageset ? 'Rename pipeline' : 'Create a new pipeline'}
                    subtitle='Name your pipeline'
                    errorMessage={this.state.errorMessage}
                    placeholder='Example: Outbound high-value leads'
                    initialValue={this.props.stageset ? this.props.stageset.name : undefined}
                    buttonText={this.props.stageset ? 'Rename pipeline' : 'Create pipeline'}
                    onClose={this.props.onClose}
                    isSaving={this.state.isSaving}
                    onSave={this.handleSave}
                />
            </form>
        );
    }

    handleSave = (pipelineName: string) => {
        this.setState({pipelineName});
    };

    handleSubmit = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();

        const newPipelineName =
            this.state.pipelineName && this.state.pipelineName.trim() !== ''
                ? this.state.pipelineName
                : 'Untitled pipeline';

        this.setState({isSaving: true});

        if (this.props.stageset) {
            // Rename instead of creating a new pipeline
            this.props
                .updateStageset({
                    name: newPipelineName,
                    id: this.props.stageset.id,
                })
                .then(() => {
                    this.setState({
                        isSaving: false,
                    });

                    this.props.onClose();
                    // Now we need to refetch the stagesets so the rest of the app knows
                })
                .then(() => Nut.managedCollections.Stagesets.fetch())
                .catch(this.handleError);
        } else {
            // Create a new pipeline
            this.props
                .createStageset({
                    name: newPipelineName,
                })
                .then((mutationResult) => {
                    this.setState({
                        isSaving: false,
                    });

                    if (!mutationResult || !mutationResult.data) return;

                    const newStagesetId = mutationResult.data.createStageset.stageset.id;
                    const newPath = `/setup/sales-automation/${newStagesetId}`;

                    appHistory.push(newPath);
                    // Now we need to refetch the stagesets so the rest of the app knows
                })
                .then(() => Nut.managedCollections.Stagesets.fetch())
                .catch(this.handleError);
        }
    };

    handleError = (err: GraphQLErrorObject) => {
        const message = safelyGetGraphQLErrorMessage(err);
        this.setState({
            errorMessage: message || undefined,
            isSaving: false,
        });
    };
}

// -----------------------------------------------------------------------------
//   CreateNewPipeline mutation
// -----------------------------------------------------------------------------

// Here's our actual graphQL mutation query code.
// Everything here is defined by the server, expect for any fragments
const CREATE_STAGESET = gql`
    mutation CreateStageset($stageset: CreateStagesetInputFieldInput!) {
        createStageset(input: {stageset: $stageset}) {
            stageset {
                ...StagesetBasic
            }
        }
    }
    ${STAGESET_BASIC}
`;

class CreateMutation extends Mutation<CreateStagesetMutation, CreateStagesetMutationVariables> {}
class RenameMutation extends Mutation<UpdateStagesetMutation, UpdateStagesetMutationVariables> {}

export const PipelineNamePopup = (props: OwnProps) => (
    <CreateMutation
        mutation={CREATE_STAGESET}
        update={(proxy, mutationResult) => {
            if (!mutationResult || !mutationResult.data) return;

            const createStageset = mutationResult.data.createStageset;

            // Read the data from our cache for this query.
            const cacheResult: StagesetsForPipelineListQuery | null | undefined = proxy.readQuery({
                query: GET_STAGESETS,
            });
            if (!cacheResult) return;

            // Add our stageset from the mutation to the end.
            cacheResult.stagesets.push({
                ...createStageset.stageset,
            });

            // Write our data back to the cache.
            proxy.writeQuery({
                query: GET_STAGESETS,
                data: cacheResult,
            });
        }}
    >
        {(createStageset) => (
            <RenameMutation
                mutation={UPDATE_STAGESET}
                update={(proxy, mutationResult) => {
                    if (!mutationResult || !mutationResult.data) return;
                    const updateStageset = mutationResult.data.updateStageset;
                    if (!updateStageset) return;

                    // Write our data back to the cache.
                    proxy.writeFragment({
                        id: updateStageset.stageset.id,
                        fragment: STAGESET_BASIC,
                        data: updateStageset.stageset,
                    });
                }}
            >
                {(rename) => (
                    <PipelineNamePopupComponent
                        {...props}
                        createStageset={(payload) =>
                            createStageset({
                                variables: {
                                    stageset: {
                                        name: payload.name,
                                    },
                                },
                            })
                        }
                        updateStageset={(payload) =>
                            rename({
                                variables: {
                                    stageset: {
                                        id: payload.id,
                                        name: payload.name,
                                    },
                                },
                            })
                        }
                    />
                )}
            </RenameMutation>
        )}
    </CreateMutation>
);
